const allowedTags = new Set([
	'p', 'div', 'span', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'del',
	'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
	'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'a', 'img',
]);
const blockedTags = new Set([
	'script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'form', 'input',
	'button', 'select', 'textarea', 'link', 'meta', 'base', 'template', 'noscript',
]);
const blockTags = new Set([
	'p', 'div', 'br', 'hr', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li',
	'blockquote', 'pre', 'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
]);

function safeUrl(value, image = false) {
	const url = String(value || '').trim();
	if (!url) return '';
	try {
		const parsed = new URL(url, document.baseURI);
		const protocols = image ? ['http:', 'https:'] : ['http:', 'https:', 'mailto:', 'tel:'];
		return protocols.includes(parsed.protocol) ? url : '';
	} catch (_) {
		return '';
	}
}

// Parse only when called in the browser. Template contents remain inert and are
// never mounted; only newly constructed, allowlisted nodes reach v-html.
export function prepareNoticeContent(value) {
	const source = String(value ?? '');
	if (typeof document === 'undefined') {
		return {
			html: source.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
			text: source,
		};
	}
	const decoder = document.createElement('textarea');
	let decoded = source;
	// Some historical messages contain double-encoded entities (e.g. &amp;ccedil;).
	for (let pass = 0; pass < 2; pass += 1) {
		decoded = decoded.replace(/&(?:#\d+|#x[\da-f]+|[a-z][\da-z]+);/gi, (entity) => {
			decoder.innerHTML = entity;
			return decoder.value;
		});
	}
	const template = document.createElement('template');
	template.innerHTML = decoded;
	const inertDocument = template.content.ownerDocument;
	const output = inertDocument.createElement('div');
	const copyNode = (node, parent) => {
		if (node.nodeType === 3) {
			parent.appendChild(inertDocument.createTextNode(node.textContent));
			return;
		}
		if (node.nodeType !== 1) return;
		const tag = node.localName.toLowerCase();
		if (blockedTags.has(tag) || node.namespaceURI !== 'http://www.w3.org/1999/xhtml') return;
		let target = parent;
		if (allowedTags.has(tag)) {
			target = inertDocument.createElement(tag);
			if (tag === 'a') {
				const href = safeUrl(node.getAttribute('href'));
				if (href) {
					target.setAttribute('href', href);
					target.setAttribute('target', '_blank');
					target.setAttribute('rel', 'noopener noreferrer');
				}
			}
			if (tag === 'img') {
				const src = safeUrl(node.getAttribute('src'), true);
				if (!src) return;
				target.setAttribute('src', src);
				target.setAttribute('alt', node.getAttribute('alt') || '');
				target.setAttribute('loading', 'lazy');
				target.setAttribute('referrerpolicy', 'no-referrer');
			}
			if (tag === 'td' || tag === 'th') {
				for (const attribute of ['colspan', 'rowspan']) {
					const span = node.getAttribute(attribute) || '';
					if (/^[1-9]\d?$/.test(span)) target.setAttribute(attribute, span);
				}
			}
			parent.appendChild(target);
		}
		for (const child of node.childNodes) copyNode(child, target);
	};
	for (const node of template.content.childNodes) copyNode(node, output);
	const textParts = [];
	const collectText = (node) => {
		if (node.nodeType === 3) textParts.push(node.textContent);
		else {
			if (blockTags.has(node.localName)) textParts.push(' ');
			for (const child of node.childNodes) collectText(child);
			if (blockTags.has(node.localName)) textParts.push(' ');
		}
	};
	collectText(output);
	return { html: output.innerHTML, text: textParts.join('').replace(/\s+/g, ' ').trim() };
}

export function noticeText(value) {
	return prepareNoticeContent(value).text;
}
